import {
  buildReclaimPartnerCodeIntakeAnswers,
  hasReclaimPartnerCodeIntakeOutcome,
  isReclaimPartnerCodeIntakeComplete,
} from "@redotech/redo-dispute-common/reclaim-partner-code-intake";
import { reclaimActivationEntrySources } from "@redotech/redo-dispute-settings-common/reclaim-access-settings";
import { ENABLE_AI_AUTOMATION_PARAM } from "../../../payments/chargebacks/disputes-settings-actions";
import {
  getReclaimShopifyPermissionsAuthorizationUrl,
  hasReclaimShopifyPermissions,
  RECLAIM_SHOPIFY_PERMISSIONS_COPY,
  RECLAIM_SHOPIFY_PERMISSIONS_STEP_ID,
} from "../../../payments/chargebacks/disputes-shopify-permissions";
import { isMerchantWalkthroughEnabled } from "../../provider/feature-flag";
import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import {
  ENABLE_AI_AUTOMATION_TASK_ID,
  RECLAIM_WALKTHROUGH_TASK_ID,
  SET_UP_BILLING_TASK_ID,
} from "./constants";
import {
  isAiAutomationEnabled,
  isDisputesBillingSatisfied,
} from "./disputes-settings-accessors";
import {
  RECLAIM_PARTNER_CODE_STEP_ID,
  ReclaimPartnerCodeStep,
  writeReclaimPartnerCodeAnswers,
} from "./reclaim-partner-code-step";

/**
 * Disputes/Reclaim setup tasks.
 *
 * Required tasks: AI automation, walkthrough, and billing setup. (Integrations
 * moved into the /welcome/chargebacks onboarding flow.)
 * Evidence window stays at the system default (5 days) for the ~80% of
 * merchants it works for; merchants with long fulfillment cycles can tune it
 * on the settings page. Keeping it out of the checklist avoids a click for
 * almost everyone.
 *
 * `disputes/enabled` itself is no longer a task — intake submit flips it
 * using the current Reclaim access defaults, so by the time the widget
 * renders the legacy "Set up Reclaim" predicate is already satisfied.
 */

const TAKE_RECLAIM_WALKTHROUGH: SetupTask = {
  id: RECLAIM_WALKTHROUGH_TASK_ID,
  title: "Take the Reclaim walkthrough",
  description:
    "Start with a quick tour of the dashboard, chargeback queue, evidence review, and automation controls.",
  visiblePredicate: isMerchantWalkthroughEnabled,
  route: (teamId) =>
    `/stores/${teamId}/payments/chargebacks/overview?walkthrough=disputes`,
  // Completed by the walkthrough's final setup-task signal.
  completionPredicate: () => false,
  requiredForLive: true,
};

const ENABLE_AI_AUTOMATION: SetupTask = {
  id: ENABLE_AI_AUTOMATION_TASK_ID,
  title: "Turn on AI evidence automation",
  description:
    "Let Reclaim AI generate and submit evidence when disputes come in.",
  actionLabel: "Enable",
  route: (teamId) =>
    `/stores/${teamId}/settings/chargebacks/reclaim?${ENABLE_AI_AUTOMATION_PARAM}=true`,
  completionPredicate: isAiAutomationEnabled,
  requiredForLive: true,
};

const SET_UP_BILLING: SetupTask = {
  id: SET_UP_BILLING_TASK_ID,
  title: "Set up billing",
  description:
    "Add a payment method so Reclaim can activate and start recovering chargebacks.",
  actionLabel: "Set up",
  route: (teamId) =>
    `/stores/${teamId}/welcome/chargebacks?modules=reclaim&resume=billing`,
  completionPredicate: isDisputesBillingSatisfied,
  requiredForLive: true,
};

const TASK_LIST = [
  ENABLE_AI_AUTOMATION,
  TAKE_RECLAIM_WALKTHROUGH,
  SET_UP_BILLING,
];

// Single ordered group — widget renders it as a flat sequential list.
const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((t) => t.id) },
];

export const disputesProductConfig: ProductSetupConfig = {
  product: "disputes",
  displayName: "Reclaim setup",
  routePattern:
    /\/(finance\/disputes|payments\/chargebacks|settings\/chargebacks)(\/|$)/,
  resumeRoute: (storeId) => `/stores/${storeId}/payments/chargebacks/overview`,
  onIntakeSubmit: async ({ entrySource, rpcClient }) => {
    const validatedEntrySource = (
      reclaimActivationEntrySources as readonly string[]
    ).includes(entrySource ?? "")
      ? (entrySource as (typeof reclaimActivationEntrySources)[number])
      : undefined;
    // This submit only runs from the intake modal (it's the disputes
    // ProductSetupConfig). If the runtime entrySource is missing or
    // unrecognized, default to "intake_modal" so the server materializes
    // the intake_modal source rule's accessOverride / pricingOverride
    // onto the team — mirrors the same fallback in intake-modal.tsx that
    // resolves displayed terms. Without this, the server short-circuits
    // and the activation skips source-rule materialization entirely.
    const result = await rpcClient.enableDisputes({
      entrySource: validatedEntrySource ?? "intake_modal",
    });
    if (result?.status === "billing_required") {
      const billingResult = await rpcClient.enableDisputesBilling({});
      if (billingResult.confirmationUrl) {
        window.location.href = billingResult.confirmationUrl;
      }
    }
  },
  intake: {
    additionalSteps: [
      {
        id: RECLAIM_PARTNER_CODE_STEP_ID,
        secondaryFooterButton: {
          onClick: ({ onContinue, setAnswer }) => {
            writeReclaimPartnerCodeAnswers({
              setAnswer,
              state: { status: "skipped_without_code" },
            });
            onContinue();
          },
          text: "Continue without code",
        },
        getSkippedAnswers: ({ answers, reclaimTerms }) =>
          reclaimTerms?.accessMode === "free" &&
          !hasReclaimPartnerCodeIntakeOutcome(answers)
            ? buildReclaimPartnerCodeIntakeAnswers({
                status: "skipped_free_terms",
              })
            : {},
        isComplete: isReclaimPartnerCodeIntakeComplete,
        render: ReclaimPartnerCodeStep,
        shouldInclude: ({ reclaimTerms }) =>
          reclaimTerms ? reclaimTerms.accessMode !== "free" : false,
      },
    ],
    welcome: {
      title: "Welcome to Reclaim",
      subtitle: "Let's defend your chargebacks automatically.",
      useDisputeStatsSubtitle: true,
    },
    finalInfoStep: {
      title: "Review Reclaim terms",
      body: "Your current Reclaim terms will be shown before activation.",
    },
    authorizationStep: {
      id: RECLAIM_SHOPIFY_PERMISSIONS_STEP_ID,
      title: RECLAIM_SHOPIFY_PERMISSIONS_COPY.title,
      body: RECLAIM_SHOPIFY_PERMISSIONS_COPY.body,
      ctaText: RECLAIM_SHOPIFY_PERMISSIONS_COPY.ctaText,
      developmentSkipCtaText: "Skip in local development",
      showWhenCompleteInDevelopment: true,
      completionPredicate: hasReclaimShopifyPermissions,
      getAuthorizationUrl: ({ rpcClient }) =>
        getReclaimShopifyPermissionsAuthorizationUrl({ rpcClient }),
      errorCopy: {
        missingAfterReturn: RECLAIM_SHOPIFY_PERMISSIONS_COPY.missingAfterReturn,
        urlError: RECLAIM_SHOPIFY_PERMISSIONS_COPY.urlError,
      },
    },
    dismissible: false,
  },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((t) => [t.id, t])),
  completion: {
    title: "Reclaim is live",
    body: "We're now defending your chargebacks automatically. You'll see updates as new disputes come in.",
    ctaText: "Got it",
  },
  relevantTeamSlice: (team) => ({
    accessScope: team.accessScope,
    disputes: team.settings?.disputes,
    walkthroughEnabled: isMerchantWalkthroughEnabled(team),
  }),
};
